import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { IEstimate, IRoom, IUserInRoom } from './esimtate.model';
import { NGXLogger as LoggerService } from "ngx-logger";
import { EstimateFdbService } from './estimate.fdb.service';
import { map } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../common/google-analytics.service';
import { PresenceService } from '../user/online-presence.service';
import { TimerService } from '../timer/timer.service';
import { SnapshotAction, snapshotChanges } from '@angular/fire/compat/database';
import { StripeService } from '../user/services/stripe.service';
import { AuthService } from '../user/auth.service';
import { UserService } from '../user/user.service';
const DEFAULT_OPTIONS: string[] = ['?', 'cof', '0', '0.5', '1', '2', '3', '5', '8', '13', '20', '40', '100'];


@Injectable()
export class EstimateService {
    private roomId = '';
    public displayedColumns: string[] = ['displayName', 'storyPoints'];
    private roomSubscription: Subscription;
    private showHideSubscription: Subscription
    private usersInRoomSubscription: Subscription
    public estimateOptions: string[];
    public showEstimates = false;
    public allowHide = true;
    public allowClear = true;
    private isOwnerAnonymous: boolean;
    public allowDeleteEstimates = true;
    public userPresence = true;
    public showAverage = true;
    public showMedian = true;
    public estimates: IEstimate[] = [];
    public usersInRoom: IUserInRoom[] = []
    public userStoryPoints: string = null;
    public alertIsClosed = false;
    public averageEstimate: string;
    public medianEstimate: string;
    public showTimer = true;
    public ownerUid: string;
    public ownerHasPremium: boolean;

    constructor(
        private estimateFireService: EstimateFdbService,
        private logger: LoggerService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private presenceService: PresenceService,
        private timeService: TimerService,
        private stripeService: StripeService,
        public authService: AuthService,
        public userService: UserService,
    ) {
        this.roomId = '';
        this.estimateOptions = this.getDefaultEstimateOptions();
    }

    public async createEstimate(roomId: string, developerName: string, uid: string, estimate?: string): Promise<void> {
        const isExistentRoom = await this.isExistentRoom(roomId); // that's not need I guess
        if (isExistentRoom) {
            if (this.isOwnerAnonymous) {
                this.googleAnalyticsService.eventEmitter('submitEstimate isOwnerAnonymous', 'Poker Room', 'submitEstimate Action');
            } else {
                this.googleAnalyticsService.eventEmitter('submitEstimate NOT isOwnerAnonymous', 'Poker Room', 'submitEstimate Action');
            }
            // make sure the user is in the list "usersInRoom"  because the miight have been cleared
            this.setUserInRoom()
            await this.estimateFireService.createEstimate(roomId, uid, estimate);
        } else {
            const error = new Error('The estimate could not be submitted. The room with id ' + roomId + ' does not exist (anymore).');
            throw (error);
        }
    }

    /**
     *   must be called always when a user wants to enter a room -resets the esimate?
     * @param roomId
     * @returns
     */
    public async enterRoom(roomId: string) {
        if (this.roomId !== roomId) { // if the user was in the same roooom before nothing needs to be done
            if (!await this.isExistentRoom(roomId)) throw new Error('Room does not exist: ' + roomId)
            this.roomId = roomId;
            this.setObservers()
            this.googleAnalyticsService.eventEmitter('Enter Room', 'Poker Room', 'enterRoom Action');
        }
        this.setUserInRoom()
    }

    private setObservers() {
        this.roomSubscription?.unsubscribe();
        this.usersInRoomSubscription?.unsubscribe();
        this.usersInRoomSubscription = this.subscribeUsersAndEstimates(this.roomId);
        this.roomSubscription = this.setRoomObserver(this.roomId);
        this.showHideSubscription = this.setShowHideObserver(this.roomId);
        this.timeService.connectRoom(this.roomId);
    }


    /**
     * Subscribes to updates for users and their estimates .
     *
     * This method sets up an observable subscription to track changes in the list of users
     * present When the user list updates, it performs the following actions:
     * - Loads the new users into the local state.
     * - Sets dummy estimates for users (for placeholder or initialization purposes).
     * - Subscribes to estimate updates for the room.
     *
     */
    private subscribeUsersAndEstimates(roomId: string) {
        return this.estimateFireService.getUsersInRoomObservable(roomId).pipe(
            map(usersInRoomSnapshot => {
                this.removeUserFromRoom(usersInRoomSnapshot)
                this.loadNewUsersInRoom(usersInRoomSnapshot);
                this.setDummyEstimates()
                this.subscribeToEstimates(roomId);
            })
        ).subscribe();
    }


    /**
     * Removes users from the local `usersInRoom` array who are no longer present in the provided snapshot.
     *
     * For each user in `usersInRoom`, checks if their `userUid` exists in the `usersInRoomSnapshot`.
     * If a user is not found in the snapshot:
     * - Unsubscribes from their estimate subscription (if any).
     * - Removes their estimate from the `estimates` array.
     * - Removes the user from the `usersInRoom` array.
     *
     * @param usersInRoomSnapshot - The current snapshot of users in the room, used to determine which users should be removed.
     */
    private removeUserFromRoom(usersInRoomSnapshot: SnapshotAction<IUserInRoom>[]): void {
        for (let i = this.usersInRoom.length - 1; i >= 0; i--) {
            const userInRoom = this.usersInRoom[i];
            const findIndex = usersInRoomSnapshot.findIndex(snapshot => snapshot.key === userInRoom.userUid);
            if (findIndex === -1) { // this user is no longer present in the snapshot
                //unsubscribe from the users estimate
                userInRoom.estimateSubscription?.unsubscribe()
                // remove users estimate from this.estimates
                this.estimates = this.estimates.filter(e => e.userUid !== userInRoom.userUid);
                // Remove userInRoom
                this.usersInRoom.splice(i, 1);
            }
        }
    }

    /**
     * Processes a snapshot of users currently in the room and adds any new users
     * to the `usersInRoom` array.
     */
    private loadNewUsersInRoom(usersInRoomSnapshot: SnapshotAction<any>[]): void {
        usersInRoomSnapshot.forEach(userSnapshot => {
            const userVal = userSnapshot.payload.val();
            const userInRoom: IUserInRoom = { ...userVal };
            userInRoom.userUid = userSnapshot.key;
            // check if there is already a user with this userUid in the array this.usersInRoom
            const existingUserIndex = this.usersInRoom.findIndex(u => u.userUid === userInRoom.userUid);
            if (existingUserIndex === -1) {
                this.logger.log('user not in room yet', userInRoom)
                this.usersInRoom.push(userInRoom)
            }
        });
    }


    /**
     * Ensures that every user in the room has an associated estimate.
     */
    private setDummyEstimates() {
        this.usersInRoom.forEach(userInRoom => {
            const estimate = this.getEstimate(userInRoom.userUid)
            if (!estimate) {
                const dummyEstimate: IEstimate = {
                    userUid: userInRoom.userUid,
                    storyPoints: "",
                    displayName: userInRoom.displayName
                }
                this.estimates.push(dummyEstimate)
            }
        })
    }

    /**
     * Subscribes to estimate updates for all users currently in the room.
     *
     * Iterates through the `usersInRoom` collection and checks if each user already has an active
     * estimate subscription. If a user does not have a subscription, it initiates
     * a new subscription for that user by calling `subscribeToUserEstimate`.
     */
    private subscribeToEstimates(roomId: string) {
        this.usersInRoom.forEach((userInRoom) => {
            if (!userInRoom.estimateSubscription) {
                this.logger.log('subscribeToEstimates not yet subscribed', userInRoom.displayName)
                this.subscribeToUserEstimate(userInRoom, roomId);
            }
        });
    }

    /**
     * This method sets up an observable subscription for the given user in the room,
     * updating the local estimate object and related properties (such as average and median estimates)
     */
    private async subscribeToUserEstimate(userInRoom: IUserInRoom, roomId: string) {
        const estimate = this.getEstimate(userInRoom.userUid)
        if (estimate) {
            userInRoom.estimateSubscription = userInRoom.estimateSubscription = this.estimateFireService.getUserEstimateObservable(roomId, estimate.userUid).pipe(
                map(estimatePayload => {
                    estimate.displayName = userInRoom.displayName
                    if (!estimatePayload?.storyPoints) {
                        estimate.storyPoints = '';
                    } else {
                        estimate.storyPoints = estimatePayload.storyPoints;
                    }
                    if (this.authService.uid === estimate.userUid) { // marke the users story points so that we can highlight the cardß
                        this.userStoryPoints = estimate.storyPoints;
                    }
                    this.averageEstimate = this.getAverageEstimate();
                    this.medianEstimate = this.getMedian();
                    if (this.showEstimates) {
                        this.estimates = [... this.estimates.sort(this.compareEstimates)];
                    } else {
                        this.estimates = [... this.estimates] // to make sure mat-table is updated
                    }
                })
            ).subscribe();
        }
    }

    private getEstimate(userUid: string): IEstimate {
        return this.estimates.find(estimate => estimate.userUid === userUid);
    }

    private async setUserInRoom() {
        if (this.userService.isLoggedIn()) {
            await this.estimateFireService.setUserInRoom(this.roomId, this.userService.getDisplayName(), this.authService.uid);
        }
    }

    async getOwnerHasPremium() {
        this.ownerHasPremium = await this.stripeService.getUserHasPremium(this.ownerUid)
        return this.ownerHasPremium
    }

    private setShowHideObserver(roomId: string): Subscription {
        return this.estimateFireService.getShowHideObservable(roomId).pipe(
            map(object => {
                if (object) {
                    this.showEstimates = object.showEstimates;
                    if (this.showEstimates) {
                        this.estimates = [...this.estimates.sort(this.compareEstimates)]; // we sort only when showing the estimates
                    }
                    else {
                        this.showEstimates = false
                    }
                }
            })).subscribe()
    }

    private setRoomObserver(roomId: string) {
        return this.roomSubscription = this.getRoomObservable(roomId).pipe(
            map(room => {
                if (room) {
                    if (this.ownerUid !== room.ownerUid) {
                        this.ownerUid = room.ownerUid;
                        this.getOwnerHasPremium();
                    }
                    if (room.estimateOptions) {
                        this.estimateOptions = room.estimateOptions.split(',');
                    } else {
                        this.estimateOptions = this.getDefaultEstimateOptions();
                    }
                    if (room.allowHide === 'no') {
                        this.allowHide = false;
                    } else {
                        this.allowHide = true;
                    }
                    this.isOwnerAnonymous = room.isOwnerAnonymous;
                    if (room.allowClear === 'no') {
                        this.allowClear = false;
                    } else {
                        this.allowClear = true;
                    }
                    if (room.allowDeleteEstimates === 'no') {
                        this.allowDeleteEstimates = false;
                    } else {
                        this.allowDeleteEstimates = true;
                    }
                    if (room.userPresence === 'yes') {
                        this.presenceService.setConnectionCallback(this.authService.uid);
                        this.userPresence = true;
                    } else {
                        this.userPresence = false;
                    }
                    if (room.showAverage === 'yes') {
                        this.showAverage = true;
                    } else {
                        this.showAverage = false;
                    }
                    if (room.showMedian === 'yes') {
                        this.showMedian = true;
                    } else {
                        this.showMedian = false;
                    }
                    if (room.showTimer === 'no') {
                        this.showTimer = false;
                    } else {
                        this.showTimer = true;
                    }
                }
            })).subscribe();
    }

    private getMedian(): string {
        let estimateNumbers: number[] = [];
        const estimatesCopy = [...this.estimates]
        estimatesCopy.sort(this.compareEstimates)
        estimatesCopy.forEach((value) => {
            const valueNumber = parseFloat(value.storyPoints);
            if (valueNumber !== undefined && valueNumber !== null && !isNaN(valueNumber)) {
                estimateNumbers.push(valueNumber)
            }
        });
        if (estimateNumbers.length === 0) { return ""; }
        else if (estimateNumbers.length % 2 === 0) {
            //even
            const lower = estimateNumbers[(estimateNumbers.length / 2) - 1]
            const higher = estimateNumbers[estimateNumbers.length / 2]
            return "" + ((higher + lower) / 2)
        } else {
            //not even
            return "" + estimateNumbers[(estimateNumbers.length / 2) - 0.5]
        }
    }

    private getAverageEstimate(): string {
        if (this.estimates) {
            let returnValue = 0;
            let index = 0;
            this.estimates.forEach((value) => {
                const valueNumber = parseFloat(value.storyPoints);
                if (valueNumber !== undefined && valueNumber !== null && !isNaN(valueNumber)) {
                    index = index + 1;
                    returnValue = returnValue + valueNumber;
                }
            });
            if (index !== 0) {
                return (Math.round(returnValue * 10 / index) / 10).toString();
            }
        }
        return '';
    }

    compareEstimates(estimateA: IEstimate, estimateB: IEstimate): number {
        // check empty case first
        const aIsEmpty = estimateA.storyPoints === undefined || estimateA.storyPoints === null || estimateA.storyPoints === ''
        const bIsEmpty = estimateB.storyPoints === undefined || estimateB.storyPoints === null || estimateB.storyPoints === ''
        if (aIsEmpty || bIsEmpty) {
            if (aIsEmpty && !bIsEmpty) {
                return -1;
            }
            if (!aIsEmpty && bIsEmpty) {
                return 1;
            }
            // both must be empty
            return 0
        }
        const a = parseFloat(estimateA.storyPoints);
        const b = parseFloat(estimateB.storyPoints);
        // case that at least one is not  a number
        if (isNaN(a) || isNaN(b)) {
            if (isNaN(a) && !isNaN(b)) {
                return -1;
            }
            if (!isNaN(a) && isNaN(b)) {
                return 1;
            }
            // both must be not a number
            if (estimateA.storyPoints < estimateB.storyPoints) {
                return -1;
            } else if (estimateA.storyPoints > estimateB.storyPoints) {
                return 1;
            } else {
                return 0;
            }
        }
        // both numbers
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    }
    private unSubscribeAllUsersEstimates(roomId: string) {
        this.usersInRoom.forEach((userInRoom) => {
            userInRoom?.estimateSubscription?.unsubscribe()
        });
    }
    public unsubscribe() {
        this.roomId = '';
        this.estimateOptions = this.getDefaultEstimateOptions();
        this.unSubscribeAllUsersEstimates(this.roomId)
        this.roomSubscription?.unsubscribe();
        this.showHideSubscription?.unsubscribe();
        this.timeService.disconnnectRoom();
    }

    public async isExistentRoom(roomId): Promise<boolean> {
        return this.estimateFireService.isExistentRoom(roomId);
    }

    public getRoomObservable(roomId: string): Observable<IRoom> {
        return this.estimateFireService.getRoomObservable(roomId);
    }

    public async setShowEstimates(roomId: string, showEstimates: boolean): Promise<void> {
        try {
            this.estimateFireService.setShowEstimates(roomId, showEstimates);
        } catch (e) {
            const error = new Error('The room with id ' + roomId + ' does not exist (anymore). ' + e);
            throw (error);
        }
    }
    public clearStoryPoints(roomId: string): void {
        this.estimateFireService.clearStoryPoints(roomId);
    }
    public clearRoom(roomId: string): void {
        this.estimateFireService.clearRoom(roomId);//
    }

    public deleteRoom(roomId: string) {
        this.estimateFireService.deleteRoom(roomId);
    }

    public deleteOldRooms(): void {
        this.estimateFireService.deleteOldRooms();
    }
    public getDefaultEstimateOptions(): string[] {
        return DEFAULT_OPTIONS;
    }
    public setRoomConfiguration(
        roomId: string,
        estimateOptions: string,
        allowHide: string,
        allowClear: string,
        allowDeleteEstimates: string,
        userPresence: string,
        showAverage: string,
        showMedian: string,
        showTimer: string)
        : void {
        this.estimateFireService.setRoomConfiguration(
            roomId,
            estimateOptions,
            allowHide,
            allowClear,
            allowDeleteEstimates,
            userPresence,
            showAverage,
            showMedian,
            showTimer);
    }
}
